/**
 * Typed destination links for an agent notification's footer.
 *
 * Route parameters are data; an agent cannot supply an arbitrary URL, label,
 * icon or variant — the closed `NotificationDestination` union decides which
 * typed route each button opens.
 *
 * @module components/inbox/NotificationRunLink
 */
import { InspectorIcon } from "../InspectorIcon";
import { WORKFLOW_RUN_NOTICE_THREAD_PREFIX, type InboxAgentNotification } from "@amoreg/contracts";
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon } from "lucide-react";
import { Button } from "../ui/button";
import { notificationDestinationLabel, type NotificationDestination } from "./inboxDetailActions";

export function NotificationRunLink({
  item,
  variant = "outline",
}: {
  readonly item: InboxAgentNotification;
  readonly variant?: "default" | "outline";
}) {
  const org = item.organizationSlug || item.organizationId;
  // A workflow's notice is filed under its run, not a chat: its footer opens the run instead.
  if (item.threadId.startsWith(WORKFLOW_RUN_NOTICE_THREAD_PREFIX)) return null;
  return (
    <Button
      size="sm"
      variant={variant}
      render={
        item.source === "inspector" ? (
          <Link
            to="/$org/compliance/inspector/$threadId"
            params={{ org, threadId: item.threadId }}
          />
        ) : (
          <Link to="/$org/$threadId" params={{ org, threadId: item.threadId }} />
        )
      }
    >
      {item.source === "inspector" ? <InspectorIcon className="size-3.5" /> : null}
      {item.source === "inspector" ? "Open inspection" : "Open research"}
    </Button>
  );
}

/** The notification's own subject: the finding, suggestion or monitor it links. */
export function NotificationDestinationLink({
  item,
  destination,
  variant = "default",
  size = "sm",
}: {
  readonly item: InboxAgentNotification;
  readonly destination: NotificationDestination;
  readonly variant?: "default" | "outline";
  readonly size?: "sm" | "xs";
}) {
  const org = item.organizationSlug || item.organizationId;
  const label = notificationDestinationLabel(destination);
  const link = () => {
    switch (destination.kind) {
      case "finding":
        return (
          <Link
            to="/$org/compliance/findings/$findingId"
            params={{ org, findingId: destination.findingId }}
          />
        );
      case "findings":
        return <Link to="/$org/compliance/findings" params={{ org }} />;
      case "suggestion":
        return (
          <Link
            to="/$org/compliance/suggestions/$recommendationId"
            params={{ org, recommendationId: destination.recommendationId }}
          />
        );
      case "suggestions":
        return <Link to="/$org/compliance/suggestions" params={{ org }} />;
      case "monitoring":
        return <Link to="/$org/compliance/regulatory" params={{ org }} />;
      case "workflow-run":
        return (
          <Link
            to="/$org/workflows/$workflowId"
            params={{ org, workflowId: destination.workflowId }}
            search={{ run: destination.runId }}
          />
        );
    }
  };
  return (
    <Button size={size} variant={variant} render={link()}>
      {label}
      <ArrowUpRightIcon />
    </Button>
  );
}
